// @ts-nocheck
/*
 * Call-Centre user edit — v1 logic preserved end-to-end (state, API
 * sequence, validation, toasts, verified-email flow). UI ported off
 * react-bootstrap onto shadcn to end the two-UI-library situation.
 */
import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { friendlyApiError } from '@/utilities/apiErrorMessage';
import { AlertTriangle, Loader2, ArrowLeft } from 'lucide-react';
import {
  API_USERS_PROFILE,
  API_SIGNUP_EMAIL_SEND_CODE,
  API_SIGNUP_EMAIL_VERIFY_CODE,
} from '@/service/url';
import { postApiCall, getApiCall } from '@/service/httpCall';
import { USER_STATUS, ROUTES, USER_ROLES } from '@/utilities/constant';
import { useAuth, errorCheck } from '@/views/dashboard/auth/auth-utils';
import { Storage } from '@/utilities/setting';
import { creatorRequiresEmailVerificationOnUserForms } from '@/utilities/userEmailVerificationAcl';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { ScrollPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { AvailabilityHint, useUserEmailAvailability } from '@/hooks/useAvailability';

const VALIDATION_MIN_PASSWORD_LENGTH = 6;
const cx = (...args) => args.filter(Boolean).join(' ');
const invalidCls = 'border-destructive focus-visible:ring-destructive';

const CallCentreUserEdit = () => {
  const { id, uid } = useParams();
  const navigate = useNavigate();
  const { checkAuth } = useAuth();

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [originalEmail, setOriginalEmail] = useState('');
  const [emailNeedsVerification, setEmailNeedsVerification] = useState(false);
  const [signupEmailOtp, setSignupEmailOtp] = useState('');
  const [signupEmailToken, setSignupEmailToken] = useState('');
  const [emailVerifiedForEdit, setEmailVerifiedForEdit] = useState(false);
  const [signupEmailBusy, setSignupEmailBusy] = useState(false);
  const [status, setStatus] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [buttonDisable, setButtonDisable] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [usernameError, setUsernameError] = useState('');
  const [nameError, setNameError] = useState('');
  const [isSuperAdminLogged, setIsSuperAdminLogged] = useState(false);

  const [, setShowToast] = useState(false);

  const emailStatus = useUserEmailAvailability(email ?? '', {
    enabled: Boolean(originalEmail) && /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email ?? ''),
    originalValue: originalEmail,
    excludeId: uid ?? null,
  });
  const [, setToastProps] = useState(null);

  const getProfileApiUrl = () => API_USERS_PROFILE.replace(':id', uid);

  useEffect(() => {
    getApiCall(getProfileApiUrl(), {}).then(onProfileGetSuccess).catch(onError);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    checkAuth(setToastProps, setShowToast);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    try {
      const raw = Storage.getItem('user');
      if (!raw) return;
      const user = JSON.parse(raw);
      setIsSuperAdminLogged(
        user?.role === USER_ROLES.SUPERADMIN || user?.role === USER_ROLES.SUB_SUPERADMIN,
      );
    } catch {
      setIsSuperAdminLogged(false);
    }
  }, []);

  function onProfileGetSuccess(resp) {
    setUsername(resp.username);
    setStatus(resp.status);
    setName(resp.name);
    setEmail(resp.email);
    setOriginalEmail(resp.email || '');
    setEmailNeedsVerification(resp.email_verified === false);
    setSignupEmailToken('');
    setSignupEmailOtp('');
    setEmailVerifiedForEdit(false);
  }

  const normalizeEmail = (value) => String(value || '').trim().toLowerCase();

  const creatorRequiresEmailVerification = () => {
    try {
      const creatorUser = JSON.parse(Storage.getItem('user') || 'null');
      return creatorRequiresEmailVerificationOnUserForms(creatorUser, {
        isSuperAdmin: isSuperAdminLogged,
        userRole: creatorUser?.role,
      });
    } catch {
      return false;
    }
  };

  const emailRequiresVerification = () => {
    if (!creatorRequiresEmailVerification()) return false;
    return (
      normalizeEmail(email) !== normalizeEmail(originalEmail) || emailNeedsVerification
    );
  };

  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEmail(value);
    validateEmail(value);
    setSignupEmailToken('');
    setSignupEmailOtp('');
    setEmailVerifiedForEdit(false);
  };

  const sendSignupEmailCode = async () => {
    if (!email.trim() || getEmailValidationError(email)) {
      toast.warning('Enter a valid email before sending a code.');
      return;
    }
    setSignupEmailBusy(true);
    try {
      await postApiCall(API_SIGNUP_EMAIL_SEND_CODE, {
        email: email.trim(),
        exclude_user_id: uid,
      });
      setSignupEmailToken('');
      setEmailVerifiedForEdit(false);
      toast.success('Verification code sent. Check the inbox for this email.');
    } catch (err) {
      toast.error(friendlyApiError(err) || 'Could not send verification code');
    } finally {
      setSignupEmailBusy(false);
    }
  };

  const verifySignupEmailOtp = async () => {
    if (!email.trim() || getEmailValidationError(email)) {
      toast.warning('Enter a valid email.');
      return;
    }
    if (!signupEmailOtp || signupEmailOtp.length !== 6) {
      toast.warning('Enter the 6-digit code from the email.');
      return;
    }
    setSignupEmailBusy(true);
    try {
      const data = await postApiCall(API_SIGNUP_EMAIL_VERIFY_CODE, {
        email: email.trim(),
        otp: signupEmailOtp.trim(),
      });
      if (data?.signupEmailToken) {
        setSignupEmailToken(data.signupEmailToken);
        setEmailVerifiedForEdit(true);
        toast.success('Email verified. You can save the user now.');
      } else {
        toast.error('Unexpected response from server');
      }
    } catch (err) {
      toast.error(friendlyApiError(err) || 'Verification failed');
      setEmailVerifiedForEdit(false);
      setSignupEmailToken('');
    } finally {
      setSignupEmailBusy(false);
    }
  };

  function validateForm() {
    let valid = true;

    if (!validateUsername(username)) {
      valid = false;
      if (!username.trim()) toast.warning('Username required');
      else toast.warning(usernameError || 'Invalid username format');
    } else if (!validateName(name)) {
      valid = false;
      if (!name.trim()) toast.warning('Name required');
      else toast.warning(nameError || 'Invalid name format');
    } else if (!status || isNaN(status)) {
      valid = false;
      toast.warning('Status required');
    } else if (password.trim() && confirmPassword.trim()) {
      if (
        password.trim().length < VALIDATION_MIN_PASSWORD_LENGTH ||
        confirmPassword.trim().length < VALIDATION_MIN_PASSWORD_LENGTH
      ) {
        valid = false;
        toast.warning(
          `Password and confirm password must be of minimum ${VALIDATION_MIN_PASSWORD_LENGTH} characters`,
        );
      } else if (password.trim() !== confirmPassword.trim()) {
        valid = false;
        toast.warning('Password and confirm password not matching');
      }
    }

    const emailValidationError = getEmailValidationError(email);
    if (emailValidationError) {
      setEmailError(emailValidationError);
      valid = false;
      toast.warning(emailValidationError);
    }

    if (emailStatus === 'taken') {
      setEmailError('Email already exists');
      valid = false;
      toast.warning('Email already exists');
    }

    if (emailRequiresVerification() && (!emailVerifiedForEdit || !signupEmailToken)) {
      valid = false;
      toast.warning(
        'Verify this email first: use Send code, then enter the OTP and click Verify OTP.',
      );
    }

    return valid;
  }

  async function submitForm() {
    setButtonDisable(true);
    if (!validateForm()) {
      setButtonDisable(false);
      return;
    }
    const body = { username, name, email, status };
    if (password) body.password = password;
    if (emailRequiresVerification() && signupEmailToken) {
      body.signup_email_token = signupEmailToken;
    }
    postApiCall(getProfileApiUrl(), body).then(onUserUpdateSuccess).catch(onError);
  }

  function onUserUpdateSuccess() {
    toast.success('User updated successfully');
    setTimeout(() => {
      const callCentreUsersListPath = ROUTES.CALL_CENTRES.USERS.LIST.replace(':id', id);
      navigate(`/${callCentreUsersListPath}`);
    }, 1500);
  }

  function onError(err) {
    if (typeof err !== 'object' && err.includes('TokenExpiredError')) {
      errorCheck(err, navigate, setToastProps, setShowToast);
    } else {
      setButtonDisable(false);
      toast.error(friendlyApiError(err) || 'Failed to update user');
    }
  }

  const validateUsername = (value) => {
    const trimmedValue = value.trim();
    if (!trimmedValue) return (setUsernameError('Username is required'), false);
    if (trimmedValue.length < 3 || trimmedValue.length > 50)
      return (setUsernameError('Username must be between 3 and 50 characters'), false);
    if (!/^[a-zA-Z0-9_]+$/.test(trimmedValue))
      return (setUsernameError('Username can only contain letters, numbers, and underscores'), false);
    setUsernameError('');
    return true;
  };

  const validateName = (value) => {
    const trimmedValue = value.trim();
    if (!trimmedValue) return (setNameError('Name is required'), false);
    if (trimmedValue.length < 2 || trimmedValue.length > 100)
      return (setNameError('Name must be between 2 and 100 characters'), false);
    if (!/^[a-zA-Z0-9_ ]+$/.test(trimmedValue))
      return (setNameError('Name can only contain letters, numbers, underscores, and spaces'), false);
    setNameError('');
    return true;
  };

  const getEmailValidationError = (value) => {
    const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!value) return 'Email is required';
    if (!emailPattern.test(value)) return 'Invalid email format';
    return '';
  };

  const validateEmail = (value) => setEmailError(getEmailValidationError(value));

  return (
    <ScrollPage>
      <PageHeader
        title="User Information"
        description="Edit this call-centre user's details."
        actions={
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate(-1)}
            className="gap-1"
          >
            <ArrowLeft className="h-4 w-4" /> Back
          </Button>
        }
      />
      <Card>
        <CardContent className="space-y-4 pt-6">
          {emailNeedsVerification && (
            <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-amber-900 dark:border-amber-900/40 dark:bg-amber-900/20 dark:text-amber-200">
              <AlertTriangle className="mt-0.5 h-4 w-4 flex-none" />
              <div className="text-sm">
                This user has not verified their email yet. Send a verification code to the email
                below and click Verify OTP before saving.
              </div>
            </div>
          )}

          <form className="space-y-4" onSubmit={(e) => e.preventDefault()}>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <label htmlFor="username" className="text-sm font-medium leading-none">
                  User Name
                </label>
                <Input
                  type="text"
                  id="username"
                  placeholder="User Name"
                  maxLength={50}
                  value={username}
                  onChange={(e) => {
                    setUsername(e.target.value);
                    validateUsername(e.target.value);
                  }}
                  onBlur={(e) => validateUsername(e.target.value)}
                  className={cx(usernameError && invalidCls)}
                />
                {usernameError && <p className="text-xs text-destructive">{usernameError}</p>}
              </div>

              <div className="space-y-2">
                <label htmlFor="name" className="text-sm font-medium leading-none">
                  Full Name
                </label>
                <Input
                  type="text"
                  id="name"
                  maxLength={100}
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value);
                    validateName(e.target.value);
                  }}
                  onBlur={(e) => validateName(e.target.value)}
                  className={cx(nameError && invalidCls)}
                  required
                />
                {nameError && <p className="text-xs text-destructive">{nameError}</p>}
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <label htmlFor="email" className="text-sm font-medium leading-none">
                  Email
                </label>
                <Input
                  type="email"
                  id="email"
                  maxLength={100}
                  value={email}
                  onChange={handleEmailChange}
                  required
                />
                {emailError && <p className="text-xs text-destructive">{emailError}</p>}
                {!emailError && (
                  <AvailabilityHint status={emailStatus} takenMessage="Email already exists" />
                )}
                {emailRequiresVerification() && (
                  <div className="mt-2 flex flex-wrap items-end gap-2">
                    {emailVerifiedForEdit ? (
                      <span className="text-xs text-emerald-600 dark:text-emerald-400">
                        Email verified
                      </span>
                    ) : (
                      <>
                        <Input
                          type="text"
                          inputMode="numeric"
                          placeholder="6-digit code"
                          maxLength={6}
                          className="max-w-[140px]"
                          value={signupEmailOtp}
                          onChange={(e) =>
                            setSignupEmailOtp(e.target.value.replace(/\D/g, '').slice(0, 6))
                          }
                          disabled={signupEmailBusy}
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          disabled={
                            signupEmailBusy || !email.trim() || !!getEmailValidationError(email)
                          }
                          onClick={sendSignupEmailCode}
                        >
                          Send code
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          disabled={signupEmailBusy || signupEmailOtp.length !== 6}
                          onClick={verifySignupEmailOtp}
                        >
                          Verify OTP
                        </Button>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <label htmlFor="pass" className="text-sm font-medium leading-none">
                  Password
                </label>
                <Input
                  type="password"
                  id="pass"
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <label htmlFor="rpass" className="text-sm font-medium leading-none">
                  Confirm Password
                </label>
                <Input
                  type="password"
                  id="rpass"
                  placeholder="Confirm Password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                />
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <label htmlFor="status" className="text-sm font-medium leading-none">
                  Status
                </label>
                <select
                  id="status"
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                >
                  <option value="">Select status</option>
                  {USER_STATUS.map((val) => (
                    <option key={val.value} value={val.value}>
                      {val.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <hr className="border-border" />

            <Button
              onClick={() => submitForm()}
              type="button"
              disabled={buttonDisable}
              className="gap-2"
            >
              Update
              {buttonDisable && <Loader2 className="h-4 w-4 animate-spin" />}
            </Button>
          </form>
        </CardContent>
      </Card>
    </ScrollPage>
  );
};

export default CallCentreUserEdit;
